import { Row, Col, Button, Form, Modal, Table, Popconfirm } from "antd";
import { useTranslation } from "react-i18next";
import AdnCard from "../common/adnCard/AdnCard";
import AdnPageTitle from "../common/adnPageTitle/AdnPageTitle";
import { PlusOutlined } from "@ant-design/icons";
import "./QuizBuilderForm.css";
import AdnDropdown from "../common/adnDropdown/AdnDropdown";
import AdnLanguageSlider from "../common/adnLanguageSlider/AdnLanguageSlider";
import AdnTextArea from "../common/adnTextArea/AdnTextArea";
import AdnInput from "../common/adnInput/AdnInput";
import moment from "moment";
import {
  MonthsPortuguese,
  QuizAnsweredBy,
  QuizStatus,
  getAnsweredBy,
} from "../common/GeneralConfigurations";
import Icon from "@ant-design/icons/lib/components/Icon";
import { ReactComponent as fluencyPT } from "../../assets/images/image_languages/icons8-portugal-48.svg";
import { ReactComponent as fluencyEN } from "../../assets/images/image_languages/icons8-great-britain-48.svg";
import { useState, useEffect, useCallback } from "react";

const QuizBuilderForm = ({
  quizForm,
  onSaveQuiz,
  title,
  loading,
  quizTypes,
  showAddGroupButton,
  handleQuizType,
  quizType,
  quiz,
  handleQuizClone,
  handleQuizReference,
  handleQuizLanguage,
  handleQuizDescription,
  cloneModalOpen,
  confirmLoading,
  handleCloneModalCancel,
  quizzesToClone,
  quizGroups,
  quizBuilderBackButton,
  handlePublishQuiz,
  showPublishButton,
  handleAddGroup,
  handleOnSelectQuizClone,
  handleIsAnsweredByConsultant,
  answerBySwitch,
  setEditQuiz,
  editQuiz,
  handleContractType,
  contractTypeOptions,
}) => {
  const { t } = useTranslation();
  const cloneColumns = [
    {
      title: t("quizBuilderForm.columns.reference"),
      dataIndex: "reference",
      key: "reference",
    },
    {
      title: t("quizBuilderForm.columns.contractType"),
      dataIndex: "contractTypeId",
      key: "contractTypeId",
      render: (text, record) =>
        record.contractTypeDescription !== null &&
        record.contractTypeDescription,
    },
    {
      title: t("quizBuilderForm.columns.language"),
      dataIndex: "language",
      key: "language",
      render: (record) =>
        record === "EN" ? (
          <Icon className="quizBuilderListLanguage" component={fluencyEN} />
        ) : (
          <Icon className="quizBuilderListLanguage" component={fluencyPT} />
        ),
    },
    {
      title: t("quizBuilderForm.columns.createdAt"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) =>
        text
          ? moment(text).format("DD") +
            "  " +
            MonthsPortuguese[parseInt(moment(text).format("MM"))] +
            " " +
            moment(text).format("YYYY")
          : undefined,
    },
    {
      title: t("quizBuilderForm.columns.status"),
      dataIndex: "status",
      key: "status",
      render(text, record) {
        return {
          children:
            record.status === 0
              ? t("quizBuilderForm.status.draft")
              : record.status === 1
              ? t("quizBuilderForm.status.published")
              : t("quizBuilderForm.status.unpublished"),
        };
      },
    },
    {
      title: t("quizBuilderForm.columns.action"),
      key: "action",
      render: (record) => (
        <Button onClick={() => handleOnSelectQuizClone(record.id)}>
          {t("quizBuilderForm.buttons.clone")}
        </Button>
      ),
    },
  ];

  const [followUpEventName, setFollowUpEventName] = useState("");

  const handleFollowUpEventId = useCallback(
    (followUpEventId) => {
      const matchedQuizType = quizTypes.find(
        (quizType) => quizType.id === followUpEventId
      );
      return matchedQuizType ? matchedQuizType.name : "";
    },
    [quizTypes]
  );

  useEffect(() => {
    if (quiz.id) {
      const result = handleFollowUpEventId(quiz.followUpEventId);
      setFollowUpEventName(result);
    }
  }, [quiz, handleFollowUpEventId]);

  const handleExpandClick = () => {
    setEditQuiz(true);
  };

  return (
    <>
      <Row justify="space-between" className="title-row">
        <Row gutter={12}>
          <Col>
            <AdnPageTitle title={title} />
          </Col>
          <Col>
            {quiz.status === QuizStatus.Draft ? (
              <div className="state-tag tag-to-do" style={{ height: "100%" }}>
                <b>{t("quizBuilderForm.status.draft")}</b>
              </div>
            ) : quiz.status === QuizStatus.Published ? (
              <div className="state-tag tag-done" style={{ height: "100%" }}>
                <b>{t("quizBuilderForm.status.published")}</b>
              </div>
            ) : (
              <div
                className="state-tag tag-canceled"
                style={{ height: "100%" }}
              >
                <b>{t("quizBuilderForm.status.unpublished")}</b>
              </div>
            )}
          </Col>
        </Row>

        <Button
          className="adn-outlined-button"
          onClick={() => quizBuilderBackButton()}
        >
          {t("quizBuilderForm.buttons.goToQuizzes")}
        </Button>
      </Row>

      <AdnCard loading={loading}>
        {editQuiz ? (
          <Form
            layout="vertical"
            name="quizForm"
            form={quizForm}
            onFinish={onSaveQuiz}
            initialValues={quiz ? quiz : []}
          >
            <Row gutter={16}>
              <AdnInput name="id" type={"hidden"} initialValues={quiz.id} />
              {quizType || quiz.id ? (
                <>
                  <Col xs={20} sm={20} md={20} lg={20} xl={10} xxl={10}>
                    <AdnDropdown
                      label={t("quizBuilderForm.labels.quiz")}
                      name="followUpEventId"
                      options={quizTypes}
                      rules={[
                        {
                          required: true,
                          message: t("quizBuilderForm.validation.quizRequired"),
                        },
                      ]}
                      handleChange={handleQuizType}
                      placeholder={t(
                        "quizBuilderForm.placeholders.chooseOption"
                      )}
                      disabled={quiz.status === QuizStatus.Published}
                    />
                  </Col>
                  <Col
                    className="button-right"
                    xs={4}
                    sm={4}
                    md={4}
                    lg={2}
                    xl={2}
                    xxl={2}
                  >
                    <Button
                      className="adn-primary-button"
                      onClick={handleQuizClone}
                      style={{ marginTop: 23, float: "left" }}
                    >
                      {t("quizBuilderForm.buttons.clone")}
                    </Button>
                  </Col>
                </>
              ) : (
                <Col xs={24} sm={24} md={24} lg={24} xl={12} xxl={12}>
                  <AdnDropdown
                    label={t("quizBuilderForm.labels.quiz")}
                    name="followUpEventId"
                    options={quizTypes}
                    rules={[
                      {
                        required: true,
                        message: t("quizBuilderForm.validation.quizRequired"),
                      },
                    ]}
                    handleChange={handleQuizType}
                    placeholder={t("quizBuilderForm.placeholders.chooseOption")}
                    disabled={quiz.status === QuizStatus.Published}
                  />
                </Col>
              )}
              <Col xs={24} sm={24} md={20} lg={12} xl={6} xxl={6}>
                <AdnInput
                  name="reference"
                  type="text"
                  placeholder={t("quizBuilderForm.placeholders.reference")}
                  label={t("quizBuilderForm.labels.reference")}
                  initialValues={quiz && quiz.name}
                  handleChange={handleQuizReference}
                  rules={[
                    {
                      required: true,
                      message: t(
                        "quizBuilderForm.validation.referenceRequired"
                      ),
                    },
                  ]}
                  disabled={quiz.status === QuizStatus.Published}
                  minLength={6}
                  maxLength={"20"}
                />
              </Col>
              <Col xs={24} sm={24} md={24} lg={12} xl={6} xxl={6}>
                <AdnDropdown
                  name="isAnsweredByConsultant"
                  label={t("quizBuilderForm.labels.quizIsAnsweredBy")}
                  handleChange={handleIsAnsweredByConsultant}
                  placeholder={t("quizBuilderForm.placeholders.answeredBy")}
                  allowClear={true}
                  value={
                    quiz &&
                    Object.keys(QuizAnsweredBy).find(
                      (key) =>
                        QuizAnsweredBy[key] === quiz.isAnsweredByConsultant
                    )
                  }
                  options={getAnsweredBy()}
                  disabled={
                    quiz.status === QuizStatus.Published || answerBySwitch
                  }
                  rules={[
                    {
                      required: true,
                      message: t(
                        "quizBuilderForm.validation.answeredByRequired"
                      ),
                    },
                  ]}
                />
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={20} sm={20} md={20} lg={20} xl={10} xxl={10}>
                <AdnDropdown
                  label={t("quizBuilderForm.labels.contractType")}
                  name="contractTypeId"
                  options={contractTypeOptions}
                  handleChange={handleContractType}
                  placeholder={t("quizBuilderForm.placeholders.contractType")}
                  disabled={quiz.status === QuizStatus.Published}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={24} md={24} lg={4} xl={2} xxl={2}>
                <AdnLanguageSlider
                  name="language"
                  label={t("quizBuilderForm.labels.language")}
                  onLanguageSwitch={() => handleQuizLanguage()}
                  isEnglish={quiz && quiz.language === "EN"}
                  disabled={quiz.status === QuizStatus.Published}
                />
              </Col>
            </Row>

            <Col span={24}>
              <AdnTextArea
                name="note"
                label={t("quizBuilderForm.labels.description")}
                maxLength={300}
                showCount={true}
                placeholder={t("quizBuilderForm.placeholders.addDescription")}
                initialValues={quiz.description}
                rows={2}
                handleChange={handleQuizDescription}
                disabled={quiz.status === QuizStatus.Published}
              ></AdnTextArea>
            </Col>

            <Row style={{ width: "100%", marginTop: "2rem" }} justify="end">
              <Modal
                title={t("quizBuilderForm.modals.selectQuizToClone")}
                size="large"
                width={700}
                open={cloneModalOpen}
                confirmLoading={confirmLoading}
                onCancel={handleCloneModalCancel}
              >
                <label>
                  <b>
                    {
                      quizTypes.find(
                        (quizType) =>
                          quizType.id ===
                          quizForm.getFieldValue("followUpEventId")
                      )?.name
                    }
                  </b>
                </label>
                <br />
                <Table columns={cloneColumns} dataSource={quizzesToClone} />
              </Modal>
              <Col>
                <Row gutter={12}>
                  {showPublishButton && (
                    <Col>
                      {quiz.status !== QuizStatus.Published && (
                        <Popconfirm
                          title={t("quizBuilderForm.confirmations.publishQuiz")}
                          onConfirm={() => handlePublishQuiz(quiz)}
                        >
                          <Button className="publishButtonQuizBuilder adn-primary-button">
                            {t("quizBuilderForm.buttons.publish")}
                          </Button>
                        </Popconfirm>
                      )}
                    </Col>
                  )}
                  <Col style={{ marginLeft: "1rem" }}>
                    {quiz.status !== QuizStatus.Published && (
                      <Button
                        type="primary"
                        htmlType="submit"
                        className="adn-primary-button"
                      >
                        {t("quizBuilderForm.buttons.saveAndClose")}
                      </Button>
                    )}
                  </Col>
                </Row>
              </Col>
            </Row>
          </Form>
        ) : (
          <>
            {quiz.id && (
              <Row
                style={{ width: "100%" }}
                justify="space-between"
                align="top"
              >
                <Col xs={24} sm={24} md={24} lg={20} xl={20} xxl={20}>
                  <Row>
                    <Col>
                      <Row
                        gutter={12}
                        align="middle"
                        style={{ marginBottom: "0.5rem" }}
                      >
                        <Col>
                          <h5
                            style={{
                              margin: 0,
                              padding: 0,
                              color: "var(--adngrey)",
                            }}
                          >
                            {quiz.isAnsweredByConsultant
                              ? t(
                                  "quizBuilderForm.answeredBy.consultantAndSupervisor"
                                )
                              : t(
                                  "quizBuilderForm.answeredBy.justSupervisor"
                                )}{" "}
                            | {quiz && quiz.language}
                          </h5>
                        </Col>
                      </Row>

                      <Row
                        gutter={12}
                        align="middle"
                        style={{ marginBottom: "1rem" }}
                      >
                        <Col>
                          <h3 style={{ margin: 0, padding: 0 }}>
                            {followUpEventName}
                          </h3>
                        </Col>
                      </Row>
                      {quiz.description ? (
                        <Row>
                          <p>{quiz && quiz.description}</p>
                        </Row>
                      ) : (
                        ""
                      )}
                    </Col>
                  </Row>
                </Col>
                {quiz.status !== QuizStatus.Published && (
                  <Col xs={24} sm={24} md={24} lg={4} xl={4} xxl={4}>
                    <Row gutter={12} justify="end">
                      {showPublishButton && (
                        <Col style={{ marginBottom: "1rem" }}>
                          {quiz.status !== QuizStatus.Published && (
                            <Popconfirm
                              title={t(
                                "quizBuilderForm.confirmations.publishQuiz"
                              )}
                              onConfirm={() => handlePublishQuiz(quiz)}
                            >
                              <Button className="publishButtonQuizBuilder adn-primary-button">
                                {t("quizBuilderForm.buttons.publish")}
                              </Button>
                            </Popconfirm>
                          )}
                        </Col>
                      )}
                      <Col>
                        <Button
                          onClick={handleExpandClick}
                          className="adn-primary-button"
                        >
                          {t("quizBuilderForm.buttons.editQuizDetails")}
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                )}
              </Row>
            )}
          </>
        )}
      </AdnCard>
      {quizGroups.length > 0 && (
        <Row style={{ width: "100%" }}>
          <Col style={{ margin: "1rem 0 0 1rem" }}>
            <h6 style={{ margin: 0, padding: 0 }}>
              {t("quizBuilderForm.labels.groups")}
            </h6>
          </Col>
        </Row>
      )}
      <Row justify="end">
        {quizGroups.length > 0 && <Col span={24}>{quizGroups}</Col>}
        {showAddGroupButton && quiz.status !== QuizStatus.Published && (
          <Col>
            <Button
              className="adn-primary-button"
              onClick={handleAddGroup}
              style={{ margin: "1rem" }}
            >
              <PlusOutlined />
              {t("quizBuilderForm.buttons.addGroup")}
            </Button>
          </Col>
        )}
      </Row>
    </>
  );
};

export default QuizBuilderForm;
